import { useEffect,useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useAppTheme } from "../../../context/ThemeContext";

export default function TeamDelegatesTable({
  delegates,
  temporaryPasswords,
  onEdit,
  onGeneratePassword,
  onWhatsApp,
  generatingPassword,
}){
  const {colors,typography,shape}=useAppTheme();
  const [page,setPage]=useState(0);
  const [rowsPerPage,setRowsPerPage]=useState(10);

  useEffect(()=>{
    const maxPage=Math.max(0,Math.ceil(delegates.length/rowsPerPage)-1);
    if(page>maxPage) setPage(maxPage);
  },[delegates.length,rowsPerPage,page]);

  const rows=delegates.slice(
    page*rowsPerPage,
    page*rowsPerPage+rowsPerPage
  );

  return (
    <Box
      sx={{
        width:"100%",
        border:`1px solid ${colors.border}`,
        borderRadius:shape.radiusLarge,
        overflow:"hidden",
        backgroundColor:colors.surface,
      }}
    >
      <TableContainer sx={{overflowX:"auto"}}>
        <Table sx={{minWidth:1050}}>
          <TableHead
            sx={{
              "& .MuiTableCell-root":{
                backgroundColor:colors.tableHeader,
                color:colors.tableHeaderText,
                borderBottom:`1px solid ${colors.tableHeader}`,
                fontWeight:typography.weights.bold,
                fontSize:typography.sizes.sm,
              },
            }}
          >
            <TableRow>
              <TableCell>Delegado</TableCell>
              <TableCell>Identificación</TableCell>
              <TableCell>Correo</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Clave</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!rows.length&&(
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{py:5}}>
                  <Typography
                    sx={{
                      color:colors.textSecondary,
                      fontSize:typography.sizes.md,
                    }}
                  >
                    No hay delegados registrados.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {rows.map((delegate)=>{
              const hasTemporaryPassword=Boolean(
                temporaryPasswords[delegate.identificacion]
              );

              return (
                <TableRow
                  key={delegate.identificacion}
                  sx={{
                    "&:last-child td":{borderBottom:0},
                    "&:hover":{
                      backgroundColor:colors.backgroundSoft,
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      sx={{
                        fontWeight:typography.weights.bold,
                        color:colors.text,
                        fontSize:typography.sizes.md,
                      }}
                    >
                      {delegate.nombre_completo||"-"}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      color:colors.textSecondary,
                      fontSize:typography.sizes.md,
                      whiteSpace:"nowrap",
                    }}
                  >
                    {delegate.identificacion||"-"}
                  </TableCell>

                  <TableCell
                    sx={{
                      color:colors.textSecondary,
                      fontSize:typography.sizes.md,
                    }}
                  >
                    {delegate.correo}
                  </TableCell>

                  <TableCell
                    sx={{
                      color:colors.textSecondary,
                      fontSize:typography.sizes.md,
                      whiteSpace:"nowrap",
                    }}
                  >
                    {delegate.telefono||"-"}
                  </TableCell>

                  <TableCell>
                    <Typography
                      sx={{
                        fontWeight:typography.weights.semibold,
                        color:delegate.active?colors.success:colors.textSecondary,
                        fontSize:typography.sizes.md,
                      }}
                    >
                      {delegate.active?"Activo":"Inactivo"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      sx={{
                        fontWeight:typography.weights.semibold,
                        color:delegate.must_change_password?colors.warning:colors.success,
                        fontSize:typography.sizes.md,
                        whiteSpace:"nowrap",
                      }}
                    >
                      {delegate.must_change_password?"Temporal":"Actualizada"}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Box
                      sx={{
                        display:"flex",
                        justifyContent:"flex-end",
                        flexWrap:"wrap",
                        gap:0.7,
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={()=>onEdit(delegate)}
                        sx={{
                          borderColor:colors.borderDark,
                          color:colors.text,
                          "&:hover":{
                            borderColor:colors.secondary,
                            color:colors.secondary,
                            backgroundColor:colors.backgroundSoft,
                          },
                        }}
                      >
                        Editar
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        disabled={generatingPassword}
                        onClick={()=>onGeneratePassword(delegate)}
                        sx={{
                          borderColor:colors.borderDark,
                          color:colors.text,
                          "&:hover":{
                            borderColor:colors.secondary,
                            color:colors.secondary,
                            backgroundColor:colors.backgroundSoft,
                          },
                        }}
                      >
                        Generar clave
                      </Button>

                      <Button
                        size="small"
                        variant="contained"
                        disabled={!hasTemporaryPassword||!delegate.telefono}
                        onClick={()=>onWhatsApp(delegate)}
                        sx={{
                          backgroundColor:colors.secondary,
                          color:colors.tableHeaderText,
                          "&:hover":{
                            backgroundColor:colors.secondaryHover,
                          },
                        }}
                      >
                        WhatsApp
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={delegates.length}
        page={page}
        onPageChange={(event,newPage)=>setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event)=>{
          setRowsPerPage(parseInt(event.target.value,10));
          setPage(0);
        }}
        rowsPerPageOptions={[5,10,25]}
        labelRowsPerPage="Filas:"
        labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`}
        sx={{
          color:colors.textSecondary,
          backgroundColor:colors.surface,
          borderTop:`1px solid ${colors.border}`,
          ".MuiTablePagination-toolbar":{
            minHeight:52,
            px:{xs:1,sm:2},
            flexWrap:{xs:"wrap",sm:"nowrap"},
            justifyContent:{xs:"center",sm:"flex-end"},
          },
          ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":{
            fontSize:typography.sizes.sm,
          },
        }}
      />
    </Box>
  );
}